import { IAddToBasket } from '@features/basket/api/basketApi.type';
import { IBaseBetTicket } from '@features/user-bets/api/userBetsApi.type';

export interface IBetEditLeg {
  eventId: number;
  alias?: string;
  date: number;
  host: string;
  guest: string | null;
  sport: string;
  description: string;
  value: string;
}

export interface IBetEditOdd extends IAddToBasket {
  id: string;
  alias?: string;
  date?: number;
  host?: string;
  guest?: string | null;
  sport?: string;
  champ?: string;
  description?: string;
  changed?: boolean;
}

export interface IBetEditState {
  ticketNo: number;
  sum: number;
  coefBefore: number;
  expressBonus?: number;
  bonusAfter?: number;
  legs: IBetEditLeg[];
  odds: IBetEditOdd[];
  coefAfter?: number;
  payout?: number;
  payoutExceedsLimit?: boolean;
  loading: boolean;
  status?: string;
  message?: string;
  messageId?: number;
  saved?: boolean;
}

export interface IEditTicketRequest {
  ticketNo: number;
  odds: IAddToBasket[];
  preview: boolean;
}

export interface IEditTicketResponse {
  status: string;
  message?: string;
  ticketNo?: number;
  sum?: number;
  coefBefore?: number;
  coefAfter?: number;
  payout?: number;
  payoutExceedsLimit?: boolean;
  expressBonus?: number | null;
  odds?: (IAddToBasket & {
    id: string;
    alias?: string;
    changed?: boolean;
    date?: number;
    host?: string;
    guest?: string | null;
    sport?: string;
    champ?: string;
    description?: string;
  })[];
  changes?: { eventId: number; outcomeSportId: number; outcomeItemId: number; value: string }[];
  eventId?: number;
  eventIds?: number[];
}

export const oddId = (odd: Pick<IAddToBasket, 'eventId' | 'outcomeSportId' | 'outcomeItemId'>) =>
  `${odd.eventId}-${odd.outcomeSportId}-${odd.outcomeItemId}`;

export const canEditTicket = (ticket?: Pick<IBaseBetTicket, 'editable' | 'statusId'> | null) =>
  !!ticket && ticket.editable === true && ticket.statusId === 4;

export const buildEditStart = (ticket: IBaseBetTicket): Omit<IBetEditState, 'odds' | 'loading'> => {
  const product = ticket.items.reduce((acc, item) => {
    const value = parseFloat(item.value);
    return isNaN(value) ? acc : acc * value;
  }, 1);
  const expressBonus = Number(ticket.props?.expressBonus);
  return {
    ticketNo: ticket.ticketNo,
    sum: ticket.sum,
    coefBefore: Math.round(product * 100) / 100,
    ...(expressBonus > 1 ? { expressBonus } : {}),
    legs: ticket.items.map((item) => ({
      eventId: item.event.id,
      alias: item.event.alias,
      date: item.event.date,
      host: item.event.host,
      guest: item.event.guest,
      sport: item.event.sport,
      description: item.description,
      value: item.value,
    })),
  };
};

const clearTotals = (edit: IBetEditState) => {
  delete edit.coefAfter;
  delete edit.payout;
  delete edit.payoutExceedsLimit;
  delete edit.bonusAfter;
};

export const toggleOdd = (edit: IBetEditState, odd: IAddToBasket) => {
  const id = oddId(odd);
  const index = edit.odds.findIndex((item) => item.id === id);
  if (index !== -1) {
    edit.odds.splice(index, 1);
  } else {
    edit.odds = edit.odds.filter((item) => item.eventId !== odd.eventId);
    edit.odds.push({ ...odd, id });
  }
  delete edit.status;
  delete edit.message;
  clearTotals(edit);
};

export const removeOdd = (edit: IBetEditState, id: string) => {
  edit.odds = edit.odds.filter((item) => item.id !== id);
  delete edit.status;
  delete edit.message;
  clearTotals(edit);
};

export const applyEditResponse = (edit: IBetEditState, request: IEditTicketRequest, payload: IEditTicketResponse, now: number) => {
  edit.loading = false;
  const requested = request.odds.map(oddId).sort().join(',');
  const current = edit.odds.map((odd) => odd.id).sort().join(',');
  if (payload.status === 'BET_EDIT_PREVIEW') {
    if (requested !== current) return;
    edit.coefAfter = payload.coefAfter;
    edit.payout = payload.payout;
    edit.payoutExceedsLimit = payload.payoutExceedsLimit;
    if (payload.expressBonus && payload.expressBonus > 1) {
      edit.bonusAfter = payload.expressBonus;
    } else {
      delete edit.bonusAfter;
    }
    (payload.odds ?? []).forEach((odd) => {
      const index = edit.odds.findIndex((item) => item.id === odd.id);
      if (index !== -1) {
        edit.odds[index] = {
          ...edit.odds[index],
          value: odd.value,
          changed: odd.changed,
          date: odd.date,
          host: odd.host,
          guest: odd.guest,
          sport: odd.sport,
          alias: odd.alias,
          champ: odd.champ,
          description: odd.description,
        };
      }
    });
    return;
  }
  if (payload.status === 'BET_EDIT_SAVED') {
    edit.saved = true;
    edit.status = payload.status;
    edit.message = payload.message;
    edit.messageId = now;
    return;
  }
  if (request.preview && (payload.eventId || payload.eventIds?.length)) {
    const bad = [ ...(payload.eventIds ?? []), ...(payload.eventId ? [ payload.eventId ] : []) ];
    edit.odds = edit.odds.filter((odd) => !bad.includes(odd.eventId));
    clearTotals(edit);
  }
  if (payload.status === 'BET_EDIT_LINE_CHANGED') {
    (payload.changes ?? []).forEach((change) => {
      const index = edit.odds.findIndex((item) => item.id === oddId(change));
      if (index !== -1) {
        edit.odds[index].value = change.value;
        edit.odds[index].changed = true;
      }
    });
  }
  edit.status = payload.status;
  edit.message = payload.message;
  edit.messageId = now;
};

const product = (values: string[]) => values.reduce((acc, item) => {
  const value = parseFloat(item);
  return isNaN(value) ? acc : acc * value;
}, 1);

export const localTotals = (edit: IBetEditState) => {
  const exact = product(edit.legs.map((leg) => leg.value)) * product(edit.odds.map((odd) => odd.value));
  const coefAfter = edit.coefAfter ?? Math.round(exact * 100) / 100;
  const bonus = edit.bonusAfter ?? edit.expressBonus;
  const payoutOdd = bonus ? Math.round(exact * bonus * 100) / 100 : coefAfter;
  return {
    coefAfter,
    payout: Math.round(edit.sum * payoutOdd * 100) / 100,
  };
};

export const requestOdds = (odds: IBetEditOdd[]): IAddToBasket[] =>
  odds.map(({ eventId, outcomeSportId, outcomeItemId, value, info }) => ({
    eventId,
    outcomeSportId,
    outcomeItemId,
    value,
    ...(info?.length ? { info } : {}),
  }));
